import { Component } from '@theme/component';

const HEADING_SELECTOR = 'h2, h3';
const ROOT_MARGIN = '-80px 0px -60% 0px';

/**
 * Builds a slug from heading text content.
 *
 * @param {string} text
 * @returns {string}
 */
function slugify(text) {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^\w一-鿿\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * @typedef {Object} TocItem
 * @property {string} id
 * @property {string} text
 * @property {'h2'|'h3'} level
 * @property {TocItem[]} [children]
 */

/**
 * <article-toc> — auto-generates a sticky table of contents from article headings.
 *
 * Scans `.article-content` for h2/h3, builds a nested TOC tree, highlights the
 * currently visible heading via IntersectionObserver, and provides a mobile
 * bottom-sheet dialog.
 *
 * @extends Component
 */
class ArticleToc extends Component {
  /** @type {TocItem[]} */
  #tree = [];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    const content = document.querySelector('.article-content');
    if (!content) return;

    const headings = /** @type {NodeListOf<HTMLHeadingElement>} */ (
      content.querySelectorAll(HEADING_SELECTOR)
    );

    if (headings.length === 0) {
      this.hidden = true;
      return;
    }

    this.#ensureHeadingIds(headings);
    this.#tree = this.#buildTree(headings);
    this.#render();

    if (this.refs.list) {
      this.#setupObserver(headings);
    }
  }

  /**
   * Assigns generated IDs to headings that lack one.
   *
   * @param {NodeListOf<HTMLHeadingElement>} headings
   */
  #ensureHeadingIds(headings) {
    const used = new Set();
    for (const h of headings) {
      if (h.id && !used.has(h.id)) {
        used.add(h.id);
        continue;
      }
      const base = slugify(h.textContent || '');
      let id = base || 'section';
      let suffix = 2;
      while (used.has(id)) {
        id = `${base}-${suffix}`;
        suffix++;
      }
      h.id = id;
      used.add(id);
    }
  }

  /**
   * Builds a nested TOC tree: h2 items contain child h3 items.
   *
   * @param {NodeListOf<HTMLHeadingElement>} headings
   * @returns {TocItem[]}
   */
  #buildTree(headings) {
    /** @type {TocItem[]} */
    const tree = [];
    /** @type {TocItem | null} */
    let currentH2 = null;

    for (const h of headings) {
      const item = {
        id: h.id,
        text: h.textContent?.trim() || '',
        level: h.tagName.toLowerCase(),
      };

      if (item.level === 'h2') {
        currentH2 = item;
        tree.push(item);
      } else if (item.level === 'h3' && currentH2) {
        if (!currentH2.children) {
          currentH2.children = [];
        }
        currentH2.children.push(item);
      } else if (item.level === 'h3') {
        tree.push(item);
      }
    }

    return tree;
  }

  /**
   * Renders the TOC tree into both the desktop sidebar list and the mobile dialog list.
   */
  #render() {
    const fragment = this.#buildListFragment();

    if (this.refs.list) {
      this.refs.list.appendChild(fragment.cloneNode(true));
    }

    if (this.refs['dialog-list']) {
      this.refs['dialog-list'].appendChild(fragment);
    }
  }

  /**
   * Builds a DocumentFragment containing all TOC list items.
   *
   * @returns {DocumentFragment}
   */
  #buildListFragment() {
    const fragment = document.createDocumentFragment();

    for (const item of this.#tree) {
      fragment.appendChild(this.#createLink(item, 'h2'));
    }

    return fragment;
  }

  /**
   * Creates a TOC link element, optionally with nested children.
   *
   * @param {TocItem} item
   * @param {string} parentLevel
   * @returns {HTMLLIElement}
   */
  #createLink(item, parentLevel) {
    const li = document.createElement('li');
    li.className = `article-toc__item article-toc__item--${item.level}`;

    const a = document.createElement('a');
    a.href = `#${item.id}`;
    a.className = `article-toc__link article-toc__link--${item.level}`;
    a.textContent = item.text;
    a.setAttribute('on:click', 'article-toc/handleTocClick');
    li.appendChild(a);

    if (item.children && item.children.length > 0) {
      const subList = document.createElement('ul');
      subList.className = 'article-toc__sublist';
      for (const child of item.children) {
        subList.appendChild(this.#createLink(child, item.level));
      }
      li.appendChild(subList);
    }

    return li;
  }

  /**
   * Sets up IntersectionObserver to highlight the currently visible heading.
   *
   * @param {NodeListOf<HTMLHeadingElement>} headings
   */
  #setupObserver(headings) {
    this.#observer = new IntersectionObserver(
      (entries) => {
        /** @type {HTMLElement | null} */
        let active = null;

        for (const entry of entries) {
          if (entry.isIntersecting) {
            active = /** @type {HTMLElement} */ (entry.target);
          }
        }

        if (!active) return;

        const id = active.id;
        const links = this.querySelectorAll('.article-toc__link');

        for (const link of links) {
          const href = link.getAttribute('href');
          if (href === `#${id}`) {
            link.classList.add('is-active');
            link.setAttribute('aria-current', 'true');
          } else {
            link.classList.remove('is-active');
            link.removeAttribute('aria-current');
          }
        }
      },
      {
        rootMargin: ROOT_MARGIN,
        threshold: 0,
      },
    );

    for (const h of headings) {
      this.#observer.observe(h);
    }
  }

  /**
   * Handles TOC link clicks: smooth scroll to heading, close mobile dialog.
   *
   * @param {Event} event
   */
  handleTocClick(event) {
    event.preventDefault();

    const link = /** @type {HTMLAnchorElement} */ (event.target);
    const href = link.getAttribute('href');
    if (!href) return;

    const id = href.slice(1);
    const target = document.getElementById(id);
    if (!target) return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    target.scrollIntoView({
      behavior: mediaQuery.matches ? 'auto' : 'smooth',
      block: 'start',
    });

    if (this.refs.dialog && this.refs.dialog.open) {
      this.refs.dialog.close();
    }
  }

  /**
   * Opens the mobile TOC dialog.
   */
  handleOpenDialog() {
    if (this.refs.dialog) {
      if (typeof this.refs.dialog.showModal === 'function') {
        this.refs.dialog.showModal();
      } else {
        this.refs.dialog.setAttribute('open', '');
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }
}

if (!customElements.get('article-toc')) {
  customElements.define('article-toc', ArticleToc);
}
